import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaClock, FaCheckCircle } from "react-icons/fa";
import { IoIosArrowDown, IoIosArrowUp } from "react-icons/io";
import ContactUsForm from "../../components/ContactForm";

const CURRENT_INTAKE_YEAR = 2026;

export default function StudyAbroadPage() {
  const [activeStep, setActiveStep] = useState(0);
  const [openVisaReason, setOpenVisaReason] = useState(null);

  const contactRef = useRef(null);
  const scrollToContact = () =>
    contactRef.current?.scrollIntoView({ behavior: "smooth" });

  /* ================= STRATEGIC TIMELINE ================= */
const timeline = [
  {
    title: "Counselling & Profile Review",
    duration: "1–2 Days",
    desc:
      "We assess academics, gaps, finances, work experience, and long-term goals to determine realistic countries and risk level.",
    risks: [
      "Applying to unsuitable countries",
      "Ignoring visa red flags early",
    ],
    actions: [
      "Profile risk categorisation",
      "Career & PR alignment mapping",
    ],
    outcome:
      "Clear direction with realistic success probability before any application.",
  },
  {
    title: "Country & University Shortlisting",
    duration: "3–5 Days",
    desc:
      "Universities are shortlisted based on approval probability, intake timelines, and return on investment — not rankings alone.",
    risks: [
      "Over-ambitious shortlisting",
      "Low visa compatibility",
    ],
    actions: [
      "Approval-history based selection",
      "Country-intake strategy",
    ],
    outcome:
      "Balanced shortlist that protects both admission and visa success.",
  },
  {
    title: "Documents & Applications",
    duration: "2–3 Weeks",
    desc:
      "SOPs, LORs, resumes, and applications are structured to justify profile logic and remove ambiguity.",
    risks: [
      "Generic SOPs",
      "Unexplained gaps or switches",
    ],
    actions: [
      "Justification-first SOP framework",
      "Gap & transition explanations",
    ],
    outcome:
      "Applications that communicate intent clearly to universities and embassies.",
  },
  {
    title: "Offer Evaluation",
    duration: "1 Week",
    desc:
      "Offers are analysed for visa strength, career relevance, PR pathways, and financial feasibility.",
    risks: [
      "Choosing fastest admit",
      "Ignoring long-term outcomes",
    ],
    actions: [
      "Offer comparison matrix",
      "ROI & visa-weight evaluation",
    ],
    outcome:
      "Final selection aligned with long-term goals, not short-term excitement.",
  },
  {
    title: "Visa Strategy & Filing",
    duration: "2–4 Weeks",
    desc:
      "Visa documentation, SOP, and finances are structured as per embassy expectations and refusal patterns.",
    risks: [
      "Weak financial explanation",
      "Inconsistent intent",
    ],
    actions: [
      "Embassy-aligned SOP drafting",
      "Funds & source justification",
    ],
    outcome:
      "Reduced refusal risk through structured, defensible filings.",
  },
  {
    title: "Pre-Departure & Beyond",
    duration: "Ongoing",
    desc:
      "We assist with accommodation, travel, compliance, and post-arrival guidance.",
    risks: [
      "Non-compliance after visa",
      "Poor onboarding decisions",
    ],
    actions: [
      "Pre-departure briefing",
      "Country-specific onboarding",
    ],
    outcome:
      "Smooth transition and compliance after visa approval.",
  },
];

  /* ================= COUNTRIES ================= */
  const countries = [
    {
      name: "Canada",
      flag: "https://flagcdn.com/ca.svg",
      badge: "Most Chosen",
      meta: "₹12–18L • 20 hrs/week • PSW",
      reason: "Best for PR & tech careers",
      link: "/country-overview/canada",
      featured: true,
    },
    {
      name: "Australia",
      flag: "https://flagcdn.com/au.svg",
      badge: "PR Friendly",
      meta: "₹15–22L • 24 hrs/week • PSW",
      reason: "Strong jobs & lifestyle",
      link: "/country-overview/australia",
    },
    {
      name: "New Zealand",
      flag: "https://flagcdn.com/nz.svg",
      badge: "Safe & Student-Friendly",
      meta: "₹14–20L • 20 hrs/week",
      reason: "Skill-shortage roles",
      link: "/country-overview/newzealand",
    },
    {
      name: "USA",
      flag: "https://flagcdn.com/us.svg",
      badge: "Top Universities",
      meta: "₹25–40L • OPT",
      reason: "STEM & research focus",
      link: "/country-overview/usa",
    },
    {
      name: "Germany",
      flag: "https://flagcdn.com/de.svg",
      badge: "Low / No Tuition",
      meta: "Low cost • 20 hrs/week",
      reason: "Engineering & manufacturing",
      link: "/country-overview/germany",
    },
    {
      name: "Europe",
      flag: "https://upload.wikimedia.org/wikipedia/commons/b/b7/Flag_of_Europe.svg",
      badge: "Multiple Countries",
      meta: "Work allowed • PSW",
      reason: "Multi-country mobility",
      link: "/country-overview/europe",
    },
  ];

  const profileTypes = [
    "Students with average academics (55–65%)",
    "Profiles with study or work gaps",
    "Applicants with multiple backlogs",
    "Students with prior visa refusals",
    "Career switchers & second-degree applicants",
    "PR-focused & settlement-oriented profiles",
  ];

  const visaRejections = [
    {
      title: "Weak SOP or unclear intent",
      desc: "SOPs rewritten to justify course, country & career relevance.",
    },
    {
      title: "Improper financial documentation",
      desc: "Funds structured and explained as per embassy expectations.",
    },
    {
      title: "Course mismatch with background",
      desc: "Academic & career alignment fixed before applying.",
    },
    {
      title: "Low academic confidence",
      desc: "Handled through smart university selection & positioning.",
    },
    {
      title: "Previous refusals",
      desc: "Refusal analysis with corrective filing strategy.",
    },
  ];

  return (
    <div className="bg-[#F8F1E9] text-[#003366] font-sans">

      {/* ================= HERO ================= */}
      <section className="relative h-[520px] bg-[url('./assets/study-banner.jpg')] bg-cover bg-center flex items-center justify-center">
        <div className="absolute inset-0 bg-black/60" />

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative bg-white/10 backdrop-blur-md border border-white/20 rounded-3xl p-12 max-w-3xl text-center"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
            Study Abroad with Confidence
          </h1>

          <p className="text-white/90 text-lg mb-6">
            Admissions, visa strategy & long-term outcomes — handled end-to-end.
          </p>

          <div className="flex justify-center gap-6 text-sm text-white/80 mb-8">
            <span>✔ Govt-registered</span>
            <span>✔ 150,000+ students</span>
            <span>✔ 95% success rate</span>
          </div>

          <div className="flex justify-center gap-4">
            {/* <Link to="/eligibility">
              <button className="px-8 py-3 bg-gradient-to-r from-[#A67C52] to-[#6A85B6] text-white rounded-xl shadow-lg hover:scale-105 transition">
                Check Eligibility
              </button>
            </Link> */}

            <button
              onClick={scrollToContact}
              className="px-8 py-3 border border-white/60 text-white rounded-xl hover:bg-white/10 transition"
            >
              Free Counselling
            </button>
          </div>
        </motion.div>
      </section>

      {/* ================= COUNTRIES ================= */}
      <section className="py-24 bg-white">
        <h2 className="text-3xl font-bold text-center mb-14">
          Top Study Destinations
        </h2>

        <div className="grid md:grid-cols-3 gap-10 max-w-6xl mx-auto px-6">
          {countries.map((c, i) => (
            <motion.a
              key={i}
              href={c.link}
              whileHover={{ y: -6 }}
              className={`relative rounded-2xl p-6 shadow transition text-center ${
                c.featured
                  ? "bg-[#F8F1E9] ring-2 ring-[#6A85B6]/40"
                  : "bg-[#F8F1E9]"
              }`}
            >
              <span className="text-xs bg-gradient-to-r from-[#A67C52] to-[#6A85B6] text-white px-3 py-1 rounded-full">
                {c.badge}
              </span>

              <img src={c.flag} alt={c.name} className="w-14 mx-auto my-4" />
              <h3 className="font-semibold">{c.name}</h3>

              <p className="text-xs text-gray-500 mt-1">
                <FaClock className="inline mr-1" /> {c.meta}
              </p>

              <p className="text-xs text-gray-600 mt-2">{c.reason}</p>

              <span className="absolute top-3 right-3 text-[10px] bg-red-100 text-red-700 px-2 py-1 rounded-full">
                Fall {CURRENT_INTAKE_YEAR}
              </span>
            </motion.a>
          ))}
        </div>
      </section>

     {/* ================= PROFILE TYPES — SPECIALIZATION POWER SECTION ================= */}
<section className="relative py-24 bg-[#F8F1E9] overflow-hidden">

  {/* === Color Mesh Background === */}
  <div className="absolute inset-0">
    <div className="absolute -top-32 -left-32 w-[420px] h-[420px] bg-[#6A85B6]/30 rounded-full blur-[120px]" />
    <div className="absolute top-1/4 -right-32 w-[420px] h-[420px] bg-[#A67C52]/30 rounded-full blur-[120px]" />
    <div className="absolute bottom-0 left-1/3 w-[360px] h-[360px] bg-[#003366]/20 rounded-full blur-[140px]" />
  </div>

  <div className="relative max-w-7xl mx-auto px-6">

    {/* === Header === */}
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55 }}
      className="text-center mb-14"
    >
      <span className="inline-flex items-center mb-4 px-5 py-2 rounded-full
                       bg-gradient-to-r from-[#A67C52] to-[#6A85B6]
                       text-white text-xs tracking-widest shadow-lg">
        SPECIALIST-LED CASE HANDLING
      </span>

      <h2 className="text-4xl md:text-5xl font-bold leading-tight mb-4">
        Profiles Others Reject.<br />
        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#A67C52] to-[#6A85B6]">
          Profiles We Win For.
        </span>
      </h2>

      <p className="text-gray-700 max-w-3xl mx-auto text-lg leading-relaxed">
        These are the profiles where most consultancies fail.
        <strong> We operate with risk-mapping, not assumptions.</strong>
      </p>
    </motion.div>

    {/* === MASTER CASE PANEL === */}
    <div className="relative bg-white rounded-[2.6rem]
                    shadow-[0_40px_130px_rgba(0,0,0,0.18)]
                    p-10 md:p-12">

      {/* Inner border */}
      <div className="absolute inset-0 rounded-[2.6rem]
                      ring-1 ring-[#003366]/10 pointer-events-none" />

      <div className="grid md:grid-cols-3 gap-10">

        {[
          {
            title: "Students with prior visa refusals",
            severity: "CRITICAL",
            color: "from-red-600 to-orange-500",
            icon: "🛡️",
            hook: "High rejection probability profile",
            outcome: "Approved after refusal analysis & corrective filing",
          },
          {
            title: "Career switchers & second-degree applicants",
            severity: "HIGH-RISK",
            color: "from-orange-500 to-amber-400",
            icon: "🧭",
            hook: "Commonly misjudged by consultants",
            outcome: "Successfully repositioned across Canada & Australia",
          },
          {
            title: "PR-focused & settlement-oriented profiles",
            severity: "HIGH-RISK",
            color: "from-orange-500 to-amber-400",
            icon: "🎯",
            hook: "Fails without long-term visa alignment",
            outcome: "PR-aligned course + visa strategy executed",
          },
          {
            title: "Low academic scores (55–65%)",
            severity: "MEDIUM",
            color: "from-emerald-500 to-green-400",
            icon: "📄",
            hook: "Rejected without justification logic",
            outcome: "Admits secured via profile justification strategy",
          },
          {
            title: "Multiple backlogs & gaps",
            severity: "HIGH-RISK",
            color: "from-orange-500 to-amber-400",
            icon: "📉",
            hook: "Embassy-sensitive profile category",
            outcome: "Gap-mapped SOPs with approval focus",
          },
          {
            title: "Work-experience heavy profiles",
            severity: "MEDIUM",
            color: "from-emerald-500 to-green-400",
            icon: "🏗️",
            hook: "Often mismatched to academic pathways",
            outcome: "Industry-aligned admits & visa approval",
          },
        ].map((item, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 26 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.07 }}
            whileHover={{ y: -8 }}
            className="group relative rounded-2xl
                       bg-gradient-to-br from-[#F8F1E9] via-white to-[#F8F1E9]
                       p-7 shadow-[0_16px_42px_rgba(0,0,0,0.16)]
                       hover:shadow-[0_32px_90px_rgba(0,0,0,0.26)]
                       transition-all duration-300">

            {/* Grounding base */}
            <div className="absolute inset-x-0 bottom-0 h-4
                            bg-[#003366]/15 rounded-b-2xl" />

            {/* Severity bar */}
            <div className={`absolute inset-x-5 -top-1.5 h-1.5 rounded-full
                             bg-gradient-to-r ${item.color}`} />

            {/* Severity badge */}
            <div className={`absolute -top-4 right-6
                             bg-gradient-to-r ${item.color}
                             text-white text-[10px] px-3 py-1 rounded-full shadow-lg`}>
              {item.severity}
            </div>

            <div className="relative flex gap-5 items-start">

              {/* Icon */}
              <div className={`w-16 h-16 rounded-2xl flex items-center justify-center
                               bg-gradient-to-br ${item.color}
                               text-white text-2xl shadow-xl
                               ring-2 ring-white/60
                               group-hover:scale-115 transition`}>
                {item.icon}
              </div>

              {/* Content */}
              <div>
                <p className="font-semibold text-[#003366] leading-snug mb-1">
                  {item.title}
                </p>

                <p className="text-xs text-[#A67C52] font-medium mb-2">
                  {item.hook}
                </p>

                <p className="text-sm text-gray-600">
                  Profile analysed using risk-mapping & approval probability logic
                </p>

                <p className="mt-3 text-xs text-gray-500 italic">
                  ✔ {item.outcome}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>
    </div>

    {/* === AUTHORITY VERDICT STRIP === */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.25 }}
      className="mt-14"
    >
      <div className="max-w-4xl mx-auto
                      bg-gradient-to-r from-[#003366] via-[#4F6FA5] to-[#A67C52]
                      rounded-2xl p-8 shadow-2xl text-center text-white">
        <p className="text-base font-semibold">
          High-risk profiles fail without strategy.
        </p>
        <p className="text-sm opacity-90 mt-1">
          We don’t guess — we map outcomes before we apply.
        </p>
      </div>
    </motion.div>

  </div>
</section>


      {/* ================= STRATEGIC STUDY ABROAD FLOW ================= */}
<section className="py-20 md:py-24 bg-white">
  <div className="max-w-6xl mx-auto px-5 md:px-6">

    {/* Header */}
    <div className="text-center mb-12">
      <h2 className="text-2xl md:text-4xl font-bold mb-3">
        How Your Application Actually Moves Forward
      </h2>
      <p className="text-gray-600 max-w-2xl mx-auto">
        A controlled decision framework — not a checklist.
      </p>
    </div>

    {/* DESKTOP */}
    <div className="hidden md:grid md:grid-cols-[320px_1fr] gap-10">

      {/* Left Steps */}
      <div className="space-y-3">
        {timeline.map((step, i) => (
          <button
            key={i}
            onClick={() => setActiveStep(i)}
            className={`w-full text-left px-5 py-4 rounded-xl border
              ${activeStep === i
                ? "bg-[#F8F1E9] border-[#A67C52]"
                : "bg-white border-gray-200 hover:bg-[#F8F1E9]/40"
              }`}
          >
            <div className="flex gap-3 items-center">
              <div className="w-8 h-8 rounded-full bg-[#A67C52] text-white flex items-center justify-center text-sm">
                {i + 1}
              </div>
              <div>
                <p className="font-medium">{step.title}</p>
                <p className="text-xs text-gray-500">{step.duration}</p>
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Right Details */}
      <div className="bg-[#F8F1E9] rounded-2xl p-8">
        <FlowDetail step={timeline[activeStep]} />
      </div>
    </div>

    {/* MOBILE */}
    <div className="md:hidden">

      {/* Step Buttons */}
      <div className="flex gap-3 overflow-x-auto mb-5">
        {timeline.map((_, i) => (
          <button
            key={i}
            onClick={() => setActiveStep(i)}
            className={`px-4 py-2 rounded-full text-sm
              ${activeStep === i
                ? "bg-[#A67C52] text-white"
                : "bg-gray-100 text-gray-600"
              }`}
          >
            Step {i + 1}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="bg-[#F8F1E9] rounded-2xl p-5">
        <FlowDetail step={timeline[activeStep]} mobile />
      </div>
    </div>

  </div>
</section>


{/* ================= VISA REJECTION — RISK DIAGNOSTICS ================= */}
<section className="py-24 bg-[#F8F1E9]">

  <div className="max-w-6xl mx-auto px-6">

    {/* Header */}
    <div className="text-center mb-14">
      <h2 className="text-3xl md:text-4xl font-bold mb-4">
        Why Visas Get Rejected — And How We Correct Them
      </h2>
      <p className="text-gray-600 max-w-2xl mx-auto">
        Visa refusals are rarely random. They follow patterns.
        We diagnose the failure point before refiling.
      </p>
    </div>

    <div className="space-y-5 max-w-4xl mx-auto">

      {visaRejections.map((v, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="bg-white rounded-2xl shadow-[0_12px_40px_rgba(0,0,0,0.15)]"
        >

          {/* Header */}
          <button
            onClick={() => setOpenVisaReason(openVisaReason === i ? null : i)}
            className="w-full flex justify-between items-center px-6 py-5"
          >
            <div>
              <p className="font-semibold text-[#003366]">
                {v.title}
              </p>
              <p className="text-xs text-gray-500">
                Common refusal trigger
              </p>
            </div>
            {openVisaReason === i ? <IoIosArrowUp /> : <IoIosArrowDown />}
          </button>

          <AnimatePresence>
            {openVisaReason === i && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="px-6 pb-6"
              >

                <div className="grid md:grid-cols-[1fr_1fr] gap-6">

                  {/* Failure */}
                  <div className="bg-red-50 border border-red-200 rounded-xl p-5">
                    <p className="text-sm font-semibold text-red-700 mb-1">
                      Why applications fail here
                    </p>
                    <p className="text-sm text-red-600">
                      Visa officers see weak logic, inconsistency, or financial
                      risk at this point.
                    </p>
                  </div>

                  {/* Fix */}
                  <div className="bg-green-50 border border-green-200 rounded-xl p-5">
                    <p className="text-sm font-semibold text-green-700 mb-1">
                      Our corrective strategy
                    </p>
                    <p className="text-sm text-green-700">
                      {v.desc}
                    </p>
                  </div>

                </div>

              </motion.div>
            )}
          </AnimatePresence>

        </motion.div>
      ))}
    </div>

  </div>
</section>


      {/* ================= CONTACT ================= */}
      <div ref={contactRef}>
        <ContactUsForm />
      </div>
    </div>
  );
}
function FlowDetail({ step }) {
  return (
    <>
      <h3 className="text-lg font-semibold mb-2">{step.title}</h3>
      <p className="text-sm text-gray-700 mb-4">{step.desc}</p>

      <div className="grid md:grid-cols-2 gap-4 mb-4">

        <div className="bg-white p-4 rounded-xl border border-red-200">
          <p className="text-sm font-semibold text-red-700 mb-2">
            Common Risks
          </p>
          <ul className="text-sm text-gray-600 space-y-1">
            {step.risks.map((r, i) => (
              <li key={i}>• {r}</li>
            ))}
          </ul>
        </div>

        <div className="bg-white p-4 rounded-xl border border-green-200">
          <p className="text-sm font-semibold text-green-700 mb-2">
            What We Do
          </p>
          <ul className="text-sm text-gray-600 space-y-1">
            {step.actions.map((a, i) => (
              <li key={i}>• {a}</li>
            ))}
          </ul>
        </div>

      </div>

      <div className="bg-white p-4 rounded-xl border-l-4 border-[#6A85B6]">
        <p className="text-sm font-semibold mb-1">Outcome</p>
        <p className="text-sm text-gray-700">{step.outcome}</p>
      </div>
    </>
  );
}